import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { ClientDashboardSetting } from "../api/clientManagement";
import { DASHBOARD_SETTINGS } from "../../../../shared/utils";
import { SettingsSection } from "./SettingsSection";
import { SectionCard } from "./SectionCard";

interface DashboardAccessTabProps {
  effectiveClientId: string | null;
  dashboardSettingsQuery: UseQueryResult<{ data: ClientDashboardSetting[] }>;
  updateDashboardSettingMutation: UseMutationResult<
    unknown,
    unknown,
    { dashboardKey: string; payload: { is_enabled?: boolean; ai_enabled?: boolean | null } }
  >;
}

export const DashboardAccessTab: React.FC<DashboardAccessTabProps> = ({
  effectiveClientId,
  dashboardSettingsQuery,
  updateDashboardSettingMutation,
}) => {
  const settingsMap = useMemo(() => {
    const map = new Map<string, ClientDashboardSetting>();
    dashboardSettingsQuery.data?.data?.forEach((s) => map.set(s.dashboard_key, s));
    return map;
  }, [dashboardSettingsQuery.data]);

  const disableControls = !effectiveClientId || updateDashboardSettingMutation.isPending;

  const enabledCount = DASHBOARD_SETTINGS.filter((d) => {
    const s = settingsMap.get(d.key);
    return s?.is_enabled ?? true;
  }).length;

  return (
    <SettingsSection>
      {!effectiveClientId && (
        <Alert severity="info">Select a client to manage dashboard access.</Alert>
      )}

      <SectionCard
        title="Dashboard visibility"
        description={`${enabledCount} of ${DASHBOARD_SETTINGS.length} dashboards enabled`}
      >
        {dashboardSettingsQuery.isLoading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading dashboard settings…
            </Typography>
          </Stack>
        ) : (
          <Grid container spacing={0}>
            {DASHBOARD_SETTINGS.map((dashboard, index) => {
              const setting = settingsMap.get(dashboard.key);
              const isEnabled = setting?.is_enabled ?? true;
              const aiEnabled = setting?.ai_enabled ?? (dashboard.aiSupported ? true : null);

              return (
                <React.Fragment key={dashboard.key}>
                  {index > 0 && <Grid size={12}><Divider /></Grid>}
                  <Grid size={12}>
                    <Box
                      sx={{
                        py: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        opacity: isEnabled ? 1 : 0.5,
                        transition: "opacity 0.15s",
                      }}
                    >
                      {/* Label + description */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                          <Typography variant="body2" fontWeight={500}>
                            {dashboard.label}
                          </Typography>
                          {dashboard.aiSupported && (
                            <Tooltip title="Supports AI features">
                              <Chip
                                icon={<AutoAwesomeIcon />}
                                label="AI"
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 20, fontSize: 11, "& .MuiChip-icon": { fontSize: 12 } }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {dashboard.description}
                        </Typography>
                      </Box>

                      {/* AI toggle (if supported) */}
                      {dashboard.aiSupported && (
                        <Tooltip title={isEnabled ? "Toggle AI features" : "Enable dashboard first"}>
                          <Stack alignItems="center" spacing={0.25}>
                            <Switch
                              size="small"
                              checked={Boolean(aiEnabled)}
                              disabled={disableControls || !isEnabled}
                              onChange={(e) =>
                                updateDashboardSettingMutation.mutate({
                                  dashboardKey: dashboard.key,
                                  payload: { ai_enabled: e.target.checked },
                                })
                              }
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                              AI
                            </Typography>
                          </Stack>
                        </Tooltip>
                      )}

                      {/* Enabled toggle */}
                      <Tooltip title={isEnabled ? "Disable dashboard" : "Enable dashboard"}>
                        <Stack alignItems="center" spacing={0.25}>
                          <Switch
                            size="small"
                            checked={isEnabled}
                            disabled={disableControls}
                            onChange={(e) =>
                              updateDashboardSettingMutation.mutate({
                                dashboardKey: dashboard.key,
                                payload: { is_enabled: e.target.checked },
                              })
                            }
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            {isEnabled ? "On" : "Off"}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    </Box>
                  </Grid>
                </React.Fragment>
              );
            })}
          </Grid>
        )}
      </SectionCard>
    </SettingsSection>
  );
};
